import { useMemo, memo, useCallback } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
  IMaterialTableProps,
} from '@uniqkey-frontend/shared-app';
import {
  GroupOrganizationsResponseModel,
} from '@uniqkey-backend-partner/api-client';
import { useNavigate } from 'react-router-dom';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import { TGetGroupOrganizationsMethod } from '../../../hooks/useGroupsAPI/interfaces';

export interface IGroupOrganizationsTableRow extends GroupOrganizationsResponseModel {}

interface IGroupOrganizationsTableProps extends
  Omit<IUseTableReturn<IGroupOrganizationsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetGroupOrganizationsMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  groupOrganizations: IUseQueryTableFetchReturn<TGetGroupOrganizationsMethod>['data'];
  selectedGroupOrganizations: IUseTableReturn<IGroupOrganizationsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  groupId: string;
  options?: IMaterialTableOptions<IGroupOrganizationsTableRow>;
}

const GroupOrganizationsTable = (props: IGroupOrganizationsTableProps) => {
  const {
    groupOrganizations,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedGroupOrganizations,
    onRowSelectionChange,
    createColumns,
    groupId,
    options,
  } = props;

  const { t } = useTranslations();
  const navigate = useNavigate();

  const columns = useMemo(() => createColumns([
    {
      title: t('groupPage.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '85%',
    },
    {
      title: t('groupPage.vat'),
      field: 'vat',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.vat}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
  ]), [createColumns, t]);

  const handleGroupOrganizationRowClick = useCallback<
    NonNullable<IMaterialTableProps<IGroupOrganizationsTableRow>['onRowClick']>
  >((event, organization) => {
    const { organizationId } = organization!;
    navigate(`${PageRouteEnum.Groups}/${groupId}/${organizationId}`);
  }, [groupId, navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={groupOrganizations}
      isLoading={isLoading}
      options={options}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedGroupOrganizations}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleGroupOrganizationRowClick}
      rowSelectByKey="organizationId"
      rowHeight={51}
    />
  );
};

export default memo(GroupOrganizationsTable);
